import type { CabinetBranchAccess, CabinetRoleOption, InvitationView } from "@planerius/contracts/cabinet-auth"
import { type ReactNode, useCallback, useState } from "react"

import type { EmployeeView } from "../../../api/directory.api"
import {
  createInvitation,
  listInvitations,
  listRoles,
  resendInvitation,
  revokeInvitation,
} from "../../../api/invitations.api"
import { isApiProblem } from "../../../api/problem"
import { JournalDialog } from "../../../journal/appointment-dialogs"
import { Badge, Button, Checkbox, SelectField, TextField } from "../../../ui"
import { useResource } from "../use-resource"
import {
  type InviteDialogForm,
  hasDialogErrors,
  inviteDialogErrors,
  inviteRequest,
  linkCaption,
  pendingAge,
  roleOptionLabel,
} from "./invite-dialog-model"
import styles from "./invite-panel.module.css"
import { MembersList } from "./members-list"

/**
 * «Доступ в кабинет» на экране сотрудников: висящие приглашения и окно «Пригласить».
 *
 * СОТРУДНИК В РАСПИСАНИИ И СОТРУДНИК С ДОСТУПОМ — РАЗНЫЕ СПИСКИ (ТЗ Б8). Мастер может
 * работать без входа, бухгалтер — входить и не стоять в журнале. Поэтому приглашение
 * живёт отдельным блоком, а не кнопкой «Добавить мастера»: та заводит карточку
 * в расписании (макет 3-Б5), и смешивать два действия значило бы раздавать доступ
 * к базе клиентов тем, кому нужна была только строка в графике.
 */

type DialogState =
  | { readonly kind: "form" }
  | { readonly kind: "link"; readonly invitation: InvitationView; readonly link: string }

export function InvitePanel(props: {
  readonly branches: readonly CabinetBranchAccess[]
  readonly employees: readonly EmployeeView[]
  readonly canInvite: boolean
  /** Люди с доступом и их карточки (4-Б8, 4-Б10). */
  readonly canManageAccess: boolean
  readonly viewerUserId: string
  readonly timeZone: string
}): ReactNode {
  const pending = useResource((signal) => listInvitations("pending", signal), "staff-invitations-pending")
  // Истёкшие — тоже здесь: экран просроченного приглашения обещает человеку «одну кнопку
  // в разделе «Сотрудники»», и эта кнопка — «Новая ссылка» у истёкшей строки.
  const expired = useResource((signal) => listInvitations("expired", signal), "staff-invitations-expired")
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const [rowProblem, setRowProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState<{ readonly id: string; readonly action: "resend" | "revoke" } | null>(null)
  const reloadAll = (): void => {
    pending.reload()
    expired.reload()
  }

  const denied = props.canInvite ? undefined : "Приглашать в кабинет может владелец или управляющий"

  const resend = async (invitation: InvitationView): Promise<void> => {
    setBusy({ id: invitation.id, action: "resend" })
    setRowProblem(null)
    try {
      const result = await resendInvitation(invitation.id)
      setDialog({ kind: "link", invitation: result.invitation, link: result.link })
      reloadAll()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setRowProblem(error.detail ?? error.title)
    } finally {
      setBusy(null)
    }
  }

  const revoke = async (invitation: InvitationView): Promise<void> => {
    setBusy({ id: invitation.id, action: "revoke" })
    setRowProblem(null)
    try {
      await revokeInvitation(invitation.id)
      reloadAll()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setRowProblem(error.detail ?? error.title)
    } finally {
      setBusy(null)
    }
  }

  const items = [...(pending.data?.items ?? []), ...(expired.data?.items ?? [])]
  const listProblem = pending.problem ?? expired.problem

  return (
    <section className={styles.panel} aria-label="Доступ в кабинет">
      <div className={styles.head}>
        <span className={styles.title}>Доступ в кабинет</span>
        <Button size="sm" variant="primary" deniedReason={denied} onClick={() => setDialog({ kind: "form" })}>
          Пригласить в кабинет
        </Button>
      </div>
      <p className={styles.hint}>
        Карточка в расписании и вход в кабинет — разные вещи: мастеру вход нужен не всегда.
      </p>

      <MembersList
        branches={props.branches}
        viewerUserId={props.viewerUserId}
        canManage={props.canManageAccess}
        timeZone={props.timeZone}
      />

      {items.length === 0 ? null : (
        <ul className={styles.list}>
          {items.map((invitation) => (
            <li key={invitation.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.contact}>{invitation.emailMasked ?? invitation.phoneMasked ?? "—"}</span>
                <span className={styles.meta}>
                  {invitation.roleName} · {pendingAge(invitation)}
                </span>
              </div>
              {invitation.status === "expired" ? <Badge tone="error">истекло</Badge> : <Badge tone="warn">ждёт</Badge>}
              <Button
                size="sm"
                deniedReason={denied}
                disabled={busy?.id === invitation.id}
                loading={busy?.id === invitation.id && busy.action === "resend"}
                onClick={() => void resend(invitation)}
              >
                Новая ссылка
              </Button>
              <Button
                size="sm"
                deniedReason={denied}
                disabled={busy?.id === invitation.id}
                loading={busy?.id === invitation.id && busy.action === "revoke"}
                onClick={() => void revoke(invitation)}
              >
                Отозвать
              </Button>
            </li>
          ))}
        </ul>
      )}
      {listProblem === null ? null : (
        <div className={styles.error} role="alert">
          Список приглашений не загрузился: {listProblem.detail ?? listProblem.title}{" "}
          <button type="button" className={styles.retry} onClick={reloadAll}>
            Повторить
          </button>
        </div>
      )}
      {rowProblem === null ? null : (
        <div className={styles.error} role="alert">
          {rowProblem}
        </div>
      )}

      {dialog === null ? null : dialog.kind === "form" ? (
        <InviteDialog
          branches={props.branches}
          employees={props.employees}
          onClose={() => setDialog(null)}
          onCreated={(invitation, link) => {
            setDialog({ kind: "link", invitation, link })
            reloadAll()
          }}
        />
      ) : (
        <LinkDialog invitation={dialog.invitation} link={dialog.link} onClose={() => setDialog(null)} />
      )}
    </section>
  )
}

function InviteDialog(props: {
  readonly branches: readonly CabinetBranchAccess[]
  readonly employees: readonly EmployeeView[]
  readonly onClose: () => void
  readonly onCreated: (invitation: InvitationView, link: string) => void
}): ReactNode {
  const roles = useResource((signal) => listRoles(signal), "cabinet-roles")
  const roleItems: readonly CabinetRoleOption[] = roles.data?.items ?? []

  const [form, setForm] = useState<InviteDialogForm>({
    contact: "",
    roleId: "",
    // «Все филиалы, включая будущие» — только осознанной отметкой: у управляющего одного
    // филиала список из одной строки, и отмеченное по умолчанию «все» раздавало бы доступ
    // к филиалам, которых он сам не видит.
    allBranches: false,
    branchIds: props.branches.length === 1 ? [props.branches[0]?.id ?? ""] : [],
    employeeId: "",
  })
  const [showErrors, setShowErrors] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const errors = showErrors ? inviteDialogErrors(form, roleItems) : null
  const rolesProblem = roles.problem
  const update = (patch: Partial<InviteDialogForm>): void => {
    setForm((current) => ({ ...current, ...patch }))
    setProblem(null)
  }

  const submit = async (): Promise<void> => {
    setShowErrors(true)
    if (hasDialogErrors(inviteDialogErrors(form, roleItems))) return
    setBusy(true)
    try {
      const result = await createInvitation(inviteRequest(form))
      props.onCreated(result.invitation, result.link)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  const selected = roleItems.find((role) => role.id === form.roleId)

  return (
    <JournalDialog title="Пригласить в кабинет" onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <TextField
          label="Почта или телефон"
          hint="Пароль человек придумает сам — мы его не придумываем и никому не сообщаем."
          inputMode="email"
          autoComplete="off"
          value={form.contact}
          error={errors?.contact ?? undefined}
          onChange={(event) => update({ contact: event.target.value })}
        />

        <SelectField
          label="Роль"
          value={form.roleId}
          error={errors?.role ?? undefined}
          hint={selected !== undefined && !selected.grantable ? (selected.notGrantableReason ?? undefined) : undefined}
          options={[
            { value: "", label: roles.loading ? "Загружаем роли…" : "Выберите роль" },
            ...roleItems.map((role) => ({
              value: role.id,
              label: role.grantable ? roleOptionLabel(role) : `${role.name} — выдаёт владелец`,
            })),
          ]}
          onChange={(event) => update({ roleId: event.target.value })}
        />

        <fieldset className={styles.branches}>
          <legend className={styles.legend}>Филиалы</legend>
          <Checkbox checked={form.allBranches} onChange={(checked) => update({ allBranches: checked })}>
            Все филиалы, включая будущие
          </Checkbox>
          {form.allBranches
            ? null
            : props.branches.map((branch) => (
                <Checkbox
                  key={branch.id}
                  checked={form.branchIds.includes(branch.id)}
                  onChange={(checked) =>
                    update({
                      branchIds: checked
                        ? [...form.branchIds, branch.id]
                        : form.branchIds.filter((id) => id !== branch.id),
                    })
                  }
                >
                  {branch.name}
                </Checkbox>
              ))}
          {errors?.branches === null || errors === null ? null : <small className={styles.error}>{errors.branches}</small>}
        </fieldset>

        <SelectField
          label="Карточка в расписании"
          hint="Если мастер уже заведён в графике, его записи станут «своими» сразу после входа."
          value={form.employeeId}
          options={[
            { value: "", label: "Не привязывать" },
            ...props.employees.map((employee) => ({ value: employee.id, label: employee.displayName })),
          ]}
          onChange={(event) => update({ employeeId: event.target.value })}
        />

        {rolesProblem === null ? null : (
          <div className={styles.error} role="alert">
            Роли не загрузились: {rolesProblem.detail ?? rolesProblem.title}{" "}
            <button type="button" className={styles.retry} onClick={roles.reload}>
              Повторить
            </button>
          </div>
        )}
        {problem === null ? null : (
          <div className={styles.error} role="alert">
            {problem}
          </div>
        )}

        <div className={styles.buttons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} loadingLabel="Выпускаем…" onClick={() => void submit()}>
            Получить ссылку
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}

function LinkDialog(props: {
  readonly invitation: InvitationView
  readonly link: string
  readonly onClose: () => void
}): ReactNode {
  const [copied, setCopied] = useState<boolean | null>(null)

  const copy = useCallback(() => {
    const run = async (): Promise<void> => {
      try {
        await navigator.clipboard.writeText(props.link)
        setCopied(true)
      } catch {
        // Буфер обмена бывает закрыт настройками браузера. Молчать нельзя: человек
        // нажал и думает, что ссылка скопирована, — а её там нет.
        setCopied(false)
      }
    }
    void run()
  }, [props.link])

  return (
    <JournalDialog title="Ссылка-приглашение" onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <span className={styles.linkLabel}>Ссылка · показывается один раз</span>
        <input className={styles.link} readOnly value={props.link} onFocus={(event) => event.currentTarget.select()} />
        <p className={styles.caption}>{linkCaption(props.invitation)}</p>
        {copied === false ? (
          <div className={styles.error} role="alert">
            Браузер не дал скопировать. Выделите ссылку в поле и скопируйте её вручную.
          </div>
        ) : null}
        <div className={styles.buttons}>
          <Button onClick={props.onClose}>Готово</Button>
          <Button variant="primary" onClick={copy}>
            {copied === true ? "Скопировано" : "Скопировать"}
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}
